import axios from 'axios';

// Определяем базовый URL в зависимости от окружения
// В production используем относительный путь для работы через nginx proxy
const API_BASE_URL = window.location.hostname === 'localhost' 
  ? 'http://localhost:5000/api'
  : '/api';

// Создаем экземпляр axios с базовыми настройками
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true
});

// Интерсептор для добавления токена к запросам
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Добавляем timestamp последнего обновления для оптимизации last_active
    const lastUpdate = localStorage.getItem('last_active_update') || '0';
    config.headers['X-Last-Update'] = lastUpdate;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Интерсептор для обработки ответов
api.interceptors.response.use(
  (response) => {
    // Сохраняем новый timestamp last_active для оптимизации обновлений
    const lastUpdate = response.headers['x-last-update'];
    if (lastUpdate) {
      localStorage.setItem('last_active_update', lastUpdate);
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Токен истек или недействителен
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Можно добавить редирект на страницу логина
    }

    // Проверка блокировки пользователя
    if (error.response?.status === 403 && error.response?.data?.error === 'USER_BLOCKED') {
      const blockData = error.response.data.data;

      // Сохраняем данные о блокировке в localStorage
      localStorage.setItem('user_blocked', JSON.stringify({
        blocked: true,
        reason: blockData.reason,
        until: blockData.until,
        permanent: blockData.permanent
      }));

      // Триггерим событие для App.tsx
      window.dispatchEvent(new CustomEvent('user-blocked', {
        detail: blockData
      }));

      console.log('🚫 Пользователь заблокирован:', blockData);
    }

    return Promise.reject(error);
  }
);

// API методы
export const gameAPI = {
  // Авторизация через Telegram
  authTelegram: (initData: string, userId?: number) =>
    api.post('/auth/telegram', { initData, userId }),

  // DEV MODE: Авторизация по telegram_id
  authDev: (telegramId: string) =>
    api.post('/auth/dev', { telegramId }),

  // Получение данных пользователя
  getUser: () =>
    api.get('/user/profile'),

  // Проверка подписки на обязательные каналы
  checkSubscription: () =>
    api.get('/user/check-subscription'),

  // Обновление профиля
  updateProfile: (data: any) =>
    api.put('/user/profile', data),

  // Обновление языка пользователя
  updateLanguage: (language: 'ru' | 'en') =>
    api.put('/user/language', { language }),

  // Получение баланса
  getBalance: () => 
    api.get('/user/balance'),
  
  // Claim (сбор монет)
  claim: () =>
    api.post('/user/claim'),
  
  getClaimStatus: () =>
    api.get('/user/claim-status'),
  
  // Стейкинг
  getStakings: () => 
    api.get('/staking/list'),
  
  createStaking: (data: { type: string; amount: number; holdBonus?: boolean }) => 
    api.post('/staking/create', data),
  
  withdrawStaking: (stakingId: number) => 
    api.post(`/staking/${stakingId}/withdraw`),
  
  renewSubscription: (stakingId: number) => 
    api.post(`/staking/${stakingId}/renew`),
  
  // Транзакции
  getTransactions: (params?: { page?: number; limit?: number }) => 
    api.get('/transactions', { params }),
  
  deposit: (amount: number) => 
    api.post('/transactions/deposit', { amount }),
  
  withdraw: (data: { amount: number; address: string }) => 
    api.post('/transactions/withdraw', data),
  
  // Рефералы
  getReferrals: () => 
    api.get('/referrals'),
  
  getReferralStats: () => 
    api.get('/referrals/stats'),
  
  // Задания
  getTasks: () => 
    api.get('/tasks/list'),
  
  getPublicTasks: () =>
    api.get('/tasks/public'),
  
  getTasksProgress: () =>
    api.get('/tasks/progress'),
  
  startTask: (taskId: number) =>
    api.post('/tasks/start', { taskId }),
  
  checkTask: (taskId: number) =>
    api.post('/tasks/check', { taskId }),
  
  completeTask: (taskId: number) => 
    api.post('/tasks/complete', { taskId }),
  
  claimTaskReward: (taskId: number) =>
    api.post(`/tasks/${taskId}/claim`),

  checkPartnerTask: (channelId: number) =>
    api.post('/tasks/check-partner', { channelId }),

  getPartnerChannels: () =>
    api.get('/tasks/partner-channels'),

  getAdsMyStats: (telegramId: string | number) =>
    api.get(`/adsgram/my-stats?userId=${telegramId}`),

  getAdsTop: () =>
    api.get('/adsgram/top'),

  sendTaddyReward: (telegramId: string | number, adId: string) =>
    api.post('/adsgram/taddy-reward', { userId: telegramId, adId }),
  
  claimRewards: () =>
    api.post('/user/claim'),
  
  // Мини-игры
  getGames: () => 
    api.get('/games'),
  
  playGame: (gameId: string, bet: number) => 
    api.post(`/games/${gameId}/play`, { bet }),
    
  getAllGames: () =>
    api.get('/minigames/all'),
    
  playMiniGame: (data: any) => 
    api.post('/minigames/play', data),
    
  // DEPRECATED: Используйте playMiniGame вместо этого метода
  // Этот endpoint не сохраняет результаты корректно
  playMiniGameBoost: (data: any) => 
    api.post('/boost/minigame/play-boost', data),
    
  getBoostStatus: () => 
    api.get('/boost/boost-status'),
    
  getMiniGameHistory: () =>
    api.get('/minigames/history'),

  getGameHistory: (gameId: string) =>
    api.get(`/games/${gameId}/history`),

  // Система попыток
  getAttemptInfo: (gameId: string) =>
    api.get(`/game-attempts/game-attempt-info/${gameId}`),

  useAttempt: (gameId: string) =>
    api.post(`/game-attempts/use-attempt/${gameId}`),

  updateSetting: (category: string, key: string, value: string) => 
    api.post('/admin/settings/update', { category, key, value_text: value }),
    
  // Тестирование игр (для админа)
  testMinigame: (data: { gameType: string; mode: string; userId: string }) =>
    api.post('/admin/test-minigame', data),
  
  // Настройки
  getSettings: () => 
    api.get('/settings'),
  
  updateSettings: (settings: any) => 
    api.put('/settings', settings),
  
  // Статистика
  getStats: () => 
    api.get('/stats'),
  
  getDailyStats: () => 
    api.get('/stats/daily'),
  
  // Курсы обмена
  getExchangeRate: () => 
    api.get('/exchange/rate'),
  
  // WebSocket для реального времени
  connectWebSocket: (userId: string) => {
    const wsUrl = process.env.NODE_ENV === 'production'
      ? `wss://${window.location.host}/ws`
      : 'ws://localhost:5000/ws';
    
    return new WebSocket(`${wsUrl}?userId=${userId}`);
  }
};

export default api;